import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import {
  useWsmProductSeriesDeleteMutation,
  useWsmProductSeriesDetailsQuery,
  useWsmProductSeriesUpdateMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { Skeleton } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import WsmSeriesDetailsPage from "../components/WsmSeriesDetailsPage/WsmSeriesDetailsPage";
import { useWsmAxisAttributes } from "../hooks/useWsmAxisAttributes";
import { useWsmBrands } from "../hooks/useWsmBrands";
import { sectionMessages } from "../messages";
import { wsmSeriesListPath } from "../urls";
import { getSeriesInput, seriesToFormData } from "../utils";

export const WsmSeriesDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { data, loading } = useWsmProductSeriesDetailsQuery({ variables: { id } });
  const { attributes, loading: attributesLoading } = useWsmAxisAttributes();
  const { brands } = useWsmBrands();
  const [updateSeries, updateSeriesOpts] = useWsmProductSeriesUpdateMutation({
    onCompleted: result => {
      if (!result.wsmProductSeriesUpdate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
      }
    },
  });
  const [deleteSeries, deleteSeriesOpts] = useWsmProductSeriesDeleteMutation({
    onCompleted: result => {
      if (!result.wsmProductSeriesDelete?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmSeriesListPath);
      }
    },
  });
  const series = data?.wsmProductSeries;

  if (loading || !series) {
    return (
      <DetailPageLayout>
        <TopNav href={wsmSeriesListPath} title={intl.formatMessage(sectionMessages.sectionTitle)} />
        <DetailPageLayout.Content>
          <Skeleton />
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  const errors = updateSeriesOpts.data?.wsmProductSeriesUpdate?.errors ?? [];

  return (
    <>
      <WsmSeriesDetailsPage
        key={series.id}
        title={series.name}
        memberCount={series.memberCount}
        attributes={attributes}
        attributesLoading={attributesLoading}
        brands={brands}
        initialData={seriesToFormData(series)}
        errors={errors}
        disabled={updateSeriesOpts.loading}
        saveButtonBarState={getMutationState(
          updateSeriesOpts.called,
          updateSeriesOpts.loading,
          errors,
        )}
        onSubmit={formData =>
          updateSeries({ variables: { id: series.id, input: getSeriesInput(formData) } })
        }
        onDelete={() => setDeleteOpen(true)}
      />
      <ActionDialog
        open={deleteOpen}
        variant="delete"
        title={intl.formatMessage(sectionMessages.seriesDeleteTitle)}
        confirmButtonState={getMutationState(deleteSeriesOpts.called, deleteSeriesOpts.loading, [])}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteSeries({ variables: { id: series.id } })}
      >
        {intl.formatMessage(sectionMessages.seriesDeleteContent, { name: series.name })}
      </ActionDialog>
    </>
  );
};

export default WsmSeriesDetailsView;
